export interface AutomationJob {
  id: string;
  job_description: string;
  creator_id: string;
  creator_name: string;
  create_ts: string;
  start_ts: string;
  end_ts: string;
  status: string;
  extra_data: any;
  workflow_id: string;
  workflow_name: string;
  dedup_config?: DedupConfig;
  post_processing_config?: PostProcessingConfig;
  table_column_schemas?: AutomationJobCustomColumnSchema[];
}
export interface AutomationJobCustomColumnSchema {
  name: string;
  col_type: string;
  description: string;
  ai_prompt: string;
}

export type PythonType =
  | 'str'
  | 'int'
  | 'float'
  | 'bool'
  | 'List[str]'
  | 'List[int]'
  | 'dict'
  | 'file'
  | 'any';

export interface WorkflowInputSignal {
  name: string;
  description: string;
  pyType: PythonType;
}

export interface WorkflowOutputSignal {
  name: string;
  description: string;
  pyType: PythonType;
  agentSource: string;
  agentSourceDescription: string;
}

export interface WorkflowBase {
  company_id: string;
  creator_id: string;
  description: string;
  id: string;
  name: string;
}

export interface WorkflowDetail {
  id: string;
  name: string;
  description: string;
  company_id: string;
  creator_id: string;
  creator_name: string;
  input_schema: WorkflowInputSignal[];
  output_schema: WorkflowOutputSignal[];
  create_ts: string;
  prospect_primary_column?: string | null;
  custom_primary_key_column?: string | null;
}

export enum AutomationStatus {
  PENDING = 'pending',
  QUEUEING = 'queueing',
  RUNNING = 'running',
  STOPPED = 'stopped',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export type DedupConfig = {
  remove_empty_flag: boolean;
  dedupe_input_flag: boolean;
  net_new_flag: boolean;
};

export type PostProcessingConfig = {
  add_to_hot_prospects: boolean;
};

export type CampaignInstructionCreate = {
  scoring_instructions: string;
  dedup_config: DedupConfig;
  post_processing_config: PostProcessingConfig;
};

export enum ColumnType {
  STRING = 'string',
  NUMBER = 'number',
  FILE = 'file'
}

export type ColumnTypeMap = Record<string, ColumnType>;

// Reserved keyword that cannot be used by users for column names
export const RESERVED_KEYWORDS = ['id'];

// Scheduled Job Period Types
export type ScheduledJobPeriod = 'daily' | 'weekly' | 'monthly';

// Scheduled Job Status Types
export type ScheduledJobStatus = 'scheduled' | 'stopped';

// Column Mapping Schema for mapping adapter output to workflow input
export interface ColumnMappingSchema {
  workflow_column: string; // Name of the workflow input column (from input_schema)
  adapter_column: string; // Name of the column from the input adapter output
}

// Input Adapter Configuration Schema
export interface InputAdapterConfigSchema {
  source_type: string; // Type of input source (e.g., 'web_scraping')
  source_config: Record<string, any>; // Source-specific configuration
  transform_config?: Record<string, any>; // Optional transformation configuration
  column_mapping: ColumnMappingSchema[]; // Mapping from input adapter output columns to workflow input columns
}

// Config Flags Schema
export interface ConfigFlagsSchema {
  dedup_config: DedupConfig;
  post_processing_config?: PostProcessingConfig;
}

// Scheduled Automation Job Create Request
export interface ScheduledAutomationJobCreate {
  name: string; // Name of the scheduled job
  workflow_id: string; // Workflow ID to execute
  input_adapter_config: InputAdapterConfigSchema; // Input adapter configuration
  config_flags: ConfigFlagsSchema; // Config flags (dedup and post-processing)
  schedule_type: string; // Schedule type: 'daily', 'weekly', or 'monthly'
}

// Scheduled Automation Job Update Request
export interface ScheduledAutomationJobUpdate {
  name?: string;
  input_adapter_config?: InputAdapterConfigSchema;
  config_flags?: ConfigFlagsSchema;
  schedule_type?: string;
  is_active?: boolean;
}

// Scheduled Job Interface (matches backend response)
export interface ScheduledJob {
  id: string;
  name: string;
  workflow_id: string;
  company_id: string;
  creator_id: string;
  creator_name: string;
  input_adapter_config: Record<string, any>; // dict from backend
  config_flags: Record<string, any>; // dict from backend
  schedule_type: string; // 'daily', 'weekly', or 'monthly'
  is_active: boolean;
  run_count: number;
  create_ts: string;
}

// Automation Job Constants
export const SALESFORCE_SYNC_POLL_INTERVAL_MS = 2000;
export const INPUT_TABLE_MAX_ROWS = 500;
export const INPUT_TABLE_FILE_TYPE_MAX_ROWS = 50;
